import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime } from '@/lib/utils';

interface RevisionEntry {
  id: string;
  revisionNumber: number;
  editedBy: string | null;
  editedAt: string;
  snapshot: unknown;
}

interface RevisionHistoryProps {
  entityId: string;
  revisionNumber?: number | null;
  type: 'requirement' | 'actual';
}

export default function RevisionHistory({ entityId, revisionNumber, type }: RevisionHistoryProps) {
  const enabled = Number(revisionNumber) > 0;
  const path = type === 'requirement' ? `/requirements/${entityId}/revisions` : `/actual-status/${entityId}/revisions`;
  const { data, isLoading } = useQuery({
    queryKey: [type === 'requirement' ? 'requirement-revisions' : 'actual-revisions', entityId],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: RevisionEntry[] }>(path);
      return response.data ?? [];
    },
    enabled,
  });

  if (!enabled) return null;

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Revision History</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-secondary">Loading revisions...</p>}
        {!isLoading && (data ?? []).length === 0 && (
          <p className="text-sm text-secondary">No earlier revision snapshots are available.</p>
        )}
        {(data ?? []).map((revision) => (
          <details key={revision.id} className="rounded border border-border p-3 text-sm">
            <summary className="cursor-pointer font-medium text-primary">
              Revision {revision.revisionNumber}
              {revision.editedBy ? ` by ${revision.editedBy}` : ''}
              {revision.editedAt ? ` on ${formatDateTime(revision.editedAt)}` : ''}
            </summary>
            <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded bg-surface p-3 text-xs text-secondary">
              {JSON.stringify(revision.snapshot, null, 2)}
            </pre>
          </details>
        ))}
      </CardContent>
    </Card>
  );
}
